import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Table, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { getDuesList, PaymentDue, getDuesPaymentPreview } from '../../../api/paymentTransactions/dues';
import { PaymentPreviewRequest } from '../../../api/tournament/paymentService';

import '../PaymentTransactions.css';

// Legacy interface for backward compatibility
interface DuePayment {
  _id: string;
  tournamentName: string;
  team: string;
  ageCategory: string;
  players: number;
  totalFee: number;
  amountPaid: number;
  paidPercentage: number;
  balanceDue: number;
  dueDate: string;
  status: 'Due' | 'Overdue' | 'Paid';
}

const Dues: React.FC = () => {
  const navigate = useNavigate();
  const [dues, setDues] = useState<PaymentDue[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState<string | null>(null); // Track which due is being processed
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const isInitialLoad = useRef(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);



  // Mock data for development (fallback)
  const mockDues: DuePayment[] = [
    {
      _id: '1',
      tournamentName: 'Thunder Hawks',
      team: 'MHOFC',
      ageCategory: 'U17',
      players: 11,
      totalFee: 10000,
      amountPaid: 7500,
      paidPercentage: 75,
      balanceDue: 2500,
      dueDate: '2025-06-12',
      status: 'Due'
    },
    {
      _id: '2',
      tournamentName: 'Thunder Hawks',
      team: 'OFC',
      ageCategory: 'U15',
      players: 15,
      totalFee: 15000,
      amountPaid: 10000,
      paidPercentage: 67,
      balanceDue: 5000,
      dueDate: '2025-06-10',
      status: 'Overdue'
    },
    {
      _id: '3',
      tournamentName: 'Thunder Hawks',
      team: 'PFC',
      ageCategory: 'U12',
      players: 12,
      totalFee: 12000,
      amountPaid: 12000,
      paidPercentage: 100,
      balanceDue: 0,
      dueDate: '2025-06-15',
      status: 'Paid'
    }
  ];

  useEffect(() => {
    // Initial load
    loadDues(1);
  }, []);

  // Load data when page changes
  useEffect(() => {
    if (currentPage > 1) {
      loadDues(currentPage);
    }
  }, [currentPage]);

  const loadDues = async (page: number = 1) => {
    try {
      if (isInitialLoad.current) {
        setLoading(true);
      } else {
        setIsPageLoading(true);
      }
      
      console.log('Loading dues for page:', page);
      
      // Call the new API endpoint with pagination
      const response = await getDuesList(page);
      console.log('Dues loaded successfully:', response);
      
      if (response && response.data && response.data.paginatedResults) {
        setDues(response.data.paginatedResults);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages || Math.ceil((response.data.totalCount || 0) / 10));
        
        // If no dues found, show appropriate message
        if (response.data.paginatedResults.length === 0) {
          setError(null); // Clear any previous errors
        }
      } else {
        console.warn('Unexpected API response structure:', response);
        setDues([]);
        setTotalCount(0);
        setTotalPages(0);
      }
      
      setLoading(false);
      setIsPageLoading(false);
      setError(null);
    } catch (error: any) {
      console.error('Error loading dues:', error);
      
      // Handle specific error messages
      let errorMessage = 'Failed to load payment dues. Please check your connection and try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Please check your permissions or contact support.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      }
      
      setError(errorMessage);
      setDues([]);
      setTotalCount(0);
      setTotalPages(0);
      setLoading(false);
      setIsPageLoading(false);
    }
  };

  const handlePageChange = async (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    
    setCurrentPage(page);
    
    // Scroll to top for better UX
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Load dues for the new page
    await loadDues(page);
  };

  const handlePayNow = async (due: PaymentDue) => {
    try {
      // Clear any previous payment errors
      setPaymentError(null);
      
      // Set payment processing state
      setPaymentProcessing(due._id);
      
      console.log('🔍 Dues Payment Flow - Starting payment preview for due:', due._id);
      console.log('🔍 Dues Payment Flow - Balance amount:', due.balanceAmount);
      
      // Step 1: Get payment preview
      console.log('🔍 Dues Payment Flow - Step 1: Getting payment preview...');
      
      // Prepare payment preview request using player IDs from dues data
      const paymentPreviewRequest: PaymentPreviewRequest = {
        registrationId: due._id, // Use the due ID as registration ID
        ageCategory: due.ageCategory,
        players: due.playerIds || [] // Use player IDs directly from dues data
      };
      
      console.log('🔍 Dues Payment Flow - Payment preview request:', paymentPreviewRequest);
      console.log('🔍 Dues Payment Flow - Tournament ID:', due.tournamentInfo._id);
      console.log('🔍 Dues Payment Flow - Registration ID:', due._id);
      
      // Call payment preview API
      const paymentPreviewResponse = await getDuesPaymentPreview(
        due.tournamentInfo._id,
        due._id,
        paymentPreviewRequest
      );
      
      console.log('🔍 Dues Payment Flow - Payment preview response:', paymentPreviewResponse);
      
      if (!paymentPreviewResponse.data) {
        throw new Error('Invalid payment preview response. Please try again.');
      }
      
      // Step 2: Navigate to payment page with payment preview data
      console.log('🔍 Dues Payment Flow - Step 2: Navigating to payment page...');
      
      // Navigate to payment page with payment preview data
      navigate(`/tournaments/${due.tournamentInfo._id}/payment`, { 
        state: { 
          source: 'dues',
          due: due,
          paymentPreview: paymentPreviewResponse.data,
          tournament: due.tournamentInfo,
          team: due.teamInfo,
          registrationId: due._id
        } 
      });
      
      setPaymentProcessing(null);
      
    } catch (error: any) {
      console.error('🔍 Dues Payment Flow - Error in payment preview process:', error);
      setPaymentProcessing(null);
      
      // Handle different types of errors
      if (error.message) {
        setPaymentError(error.message);
      } else {
        setPaymentError('Payment preview failed. Please try again.');
      }
    }
  };



  const getPaymentStatus = (due: PaymentDue): 'Due' | 'Overdue' | 'Paid' => {
    if (due.balanceAmount === 0) return 'Paid';
    
    const now = new Date();
    const nextDueDate = due.milestoneDueDates.find(milestone => 
      new Date(milestone.dueDate) > now
    );
    
    if (!nextDueDate) return 'Overdue';
    return 'Due';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Due':
        return <span className="badge-due">Due</span>;
      case 'Overdue':
        return <span className="badge-overdue">Overdue</span>;
      case 'Paid':
        return <span className="badge-paid">Paid</span>;
      default:
        return <span className="badge-paid">{status}</span>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getNextDueDate = (due: PaymentDue) => {
    // If total balance is zero, don't show due date
    if (due.balanceAmount === 0) {
      return '-';
    }
    
    const now = new Date();
    
    // If milestoneCompleted is M1, show M2 due date
    // If milestoneCompleted is M2, show M3 due date
    // If milestoneCompleted is M3, show N/A (all milestones completed)
    
    let targetMilestone = '';
    if (due.milestoneCompleted === 'M1') {
      targetMilestone = 'M2';
    } else if (due.milestoneCompleted === 'M2') {
      targetMilestone = 'M3';
    } else if (due.milestoneCompleted === 'M3') {
      return 'N/A'; // All milestones completed
    } else {
      // If no milestone completed, show M1 due date
      targetMilestone = 'M1';
    }
    
    const nextDue = due.milestoneDueDates.find(milestone => 
      milestone.milestone === targetMilestone
    );
    
    return nextDue ? formatDate(nextDue.dueDate) : 'N/A';
  };









  if (loading || isPageLoading) {
    return (
      <div className="dues-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs 
              items={[
                {
                  label: 'Payment & Transactions',
                  active: true
                }
              ]} 
            />
          </div>

          {/* Page Header */}
          <div className="d-flex justify-content-between align-items-center mb-4" style={{ padding: '0 15px' }}>
            <div>
              <h4 className="mb-2">Payment Dues</h4>
              <p className="text-muted mb-0">Manage and track all payment dues</p>
            </div>
          </div>

          {/* Shimmer Table */}
          <Card>
            <Card.Body style={{ padding: '0' }}>
              <ShimmerLoader type="dues-table" rows={5} />
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="dues-page" style={{ marginTop: '20px' }}>

      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              {
                label: 'Payment & Transactions',
                active: true
              }
            ]} 
          />
        </div>

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4" style={{ padding: '0 15px' }}>
          <div>
            <h4 className="mb-2">Payment Dues</h4>
            <p className="text-muted mb-0">Manage and track all payment dues ({totalCount} records)</p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4" style={{ padding: '0 15px' }}>
            <div className="alert alert-danger" role="alert">
              <i className="ph-warning me-2"></i>
              {error}
              <button 
                type="button" 
                className="btn-close float-end" 
                onClick={() => setError(null)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        )}

        {/* Payment Error Display */}
        {paymentError && (
          <div className="mb-4" style={{ padding: '0 15px' }}>
            <div className="alert alert-danger" role="alert">
              <i className="ph-warning me-2"></i>
              {paymentError}
              <button 
                type="button" 
                className="btn-close float-end" 
                onClick={() => setPaymentError(null)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        )}

        {/* Dues Table */}
        <Card>
          <Card.Body style={{ padding: '0' }}>
            {isPageLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table responsive className="table-hover dues-table">
                <thead>
                  <tr>
                    <th>Tournament Name</th>
                    <th>Teams</th>
                    <th>Age Category</th>
                    <th>Total Amount</th>
                    <th>Amount Paid</th>
                    <th>Total Balance</th>
                    <th>Upcoming Due</th>
                    <th>Due Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dues.map((due) => (
                    <tr key={due._id} className="due-row">
                      <td>
                        <span>{due.tournamentInfo.name}</span>
                      </td>
                      <td className="team-cell">{due.teamInfo.shortName}</td>
                      <td className="age-cell">{due.ageCategory}</td>
                      <td className="currency-amount">{formatCurrency(due.totalAmount)}</td>
                      <td className="currency-amount">{formatCurrency(due.amountPaid)}</td>
                      <td className="currency-amount">{formatCurrency(due.balanceAmount)}</td>
                      <td className="currency-amount">{due.balanceAmountNextMilestone ? formatCurrency(due.balanceAmountNextMilestone) : '-'}</td>
                      <td className="date-format">{getNextDueDate(due)}</td>
                      <td className="actions-cell">
                        <div className="d-flex gap-2 align-items-center">
                          {/* Pay Now Button */}
                          {getPaymentStatus(due) !== 'Paid' && (
                            <button 
                              className="btn-pay-now"
                              onClick={() => handlePayNow(due)}
                              disabled={paymentProcessing === due._id}
                            >
                              {paymentProcessing === due._id ? (
                                <>
                                  <i className="ph-spinner ph-spin me-2"></i>
                                  Processing...
                                </>
                              ) : (
                                'Pay Dues'
                              )}
                            </button>
                          )}
                          

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {!loading && !isPageLoading && dues.length === 0 && !error && (
              <div className="text-center py-5">
                <i className="ph-file-text text-muted" style={{ fontSize: '3rem' }}></i>
                <p className="text-muted mt-3">No dues found</p>
                <p className="text-muted small">You don't have any pending payment dues at the moment</p>
              </div>
            )}
            
            {error && !loading && !isPageLoading && (
              <div className="text-center py-5">
                <i className="ph-warning text-danger" style={{ fontSize: '3rem' }}></i>
                <p className="text-danger mt-3">Failed to load payment dues</p>
                <p className="text-muted small mb-3">Please check your connection and try again</p>
                <Button 
                  variant="primary" 
                  onClick={() => {
                    setError(null);
                    loadDues(currentPage);
                  }}
                >
                  <i className="ph-arrow-clockwise me-2"></i>
                  Retry
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Pagination Controls */}
        {totalPages > 1 && dues.length > 0 && !error && (
          <div className="pagination-section mt-4" style={{ 
            padding: '16px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Page Info */}
            <div className="page-info mb-3">
              <span className="text-muted" style={{ 
                fontSize: '14px', 
                fontWeight: '500',
                color: '#6c757d'
              }}>
                Showing {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalCount)} of {totalCount} dues
              </span>
            </div>

            {/* Pagination Controls */}
            <div className="pagination-controls d-flex align-items-center justify-content-center">
              {/* Previous Button */}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isPageLoading}
                style={{ 
                  minWidth: '90px', 
                  height: '36px',
                  fontSize: '13px',
                  borderColor: currentPage === 1 ? '#e9ecef' : '#ced4da',
                  backgroundColor: currentPage === 1 ? '#f8f9fa' : '#ffffff',
                  color: currentPage === 1 ? '#adb5bd' : '#495057',
                  borderRadius: '6px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="ri-arrow-left-s-line me-1"></i>
                Previous
              </Button>

              {/* Page Numbers */}
              <div className="d-flex align-items-center mx-3">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      disabled={isPageLoading}
                      style={{ 
                        minWidth: '36px', 
                        height: '36px',
                        fontSize: '13px',
                        margin: '0 2px',
                        borderRadius: '6px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              {/* Next Button */}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isPageLoading}
                style={{ 
                  minWidth: '90px', 
                  height: '36px',
                  fontSize: '13px',
                  borderColor: currentPage === totalPages ? '#e9ecef' : '#ced4da',
                  backgroundColor: currentPage === totalPages ? '#f8f9fa' : '#ffffff',
                  color: currentPage === totalPages ? '#adb5bd' : '#495057',
                  borderRadius: '6px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                Next
                <i className="ri-arrow-right-s-line ms-1"></i>
              </Button>
            </div>
          </div>
        )}


      </Container>


    </div>
  );
};

export default Dues; 